<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('socialMediaPost.createPost') }}
        </v-btn>
      </div>
    </div>

    <v-row>
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :search="search"
          :items="postsComputed"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          no-data-text=""
          class="p-no-borders"
          hide-default-footer
          @page-count="pageCount = $event"
          @click:row="openViewDialog"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('socialMediaPost.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-slot:[`item.channels`]="{ item }">
            <v-tooltip
              v-for="p of item.channels"
              :key="p.channelId"
              bottom
            >
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <p-social-media-icon
                    :id="p.socialMediaId"
                    class="mr-2"
                    large
                  ></p-social-media-icon>
                </span>
              </template>
              <span>{{ p.name }}</span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.message`]="{ item }">
            <div v-html="item.message" class="p-truncated-text" style="max-width:500px"></div>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions p-visible-on-row-hover">
              <template v-if="hasAccess()">
                <p-btn-icon type="edit" @click="openEditDialog(item)"></p-btn-icon>
                <p-btn-icon type="delete" @click="deletePost(item)"></p-btn-icon>
              </template>
              <template v-else>
                <p-btn-icon type="view" @click="openViewDialog(item)"></p-btn-icon>
              </template>
            </div>
          </template>
          <template v-if="hasAccess()" v-slot:[`no-data`]>
            <p-no-data
              v-if="socialMediaPostsLoadedByProject(currentProjectId)"
              :text="$t('socialMediaPost.noPosts')"
              :btn-text="$t('socialMediaPost.createPost')"
              img="post.png"
              class="p-no-data-mt"
              @btn-click="openEditDialog()"
            ></p-no-data>
          </template>
        </v-data-table>

        <v-pagination
          v-if="postsComputed.length > 0"
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <post-view-dialog
      :dialog.sync="viewDialog"
      :post="viewDialog_post"
    ></post-view-dialog>

    <post-edit-dialog
      :dialog.sync="editDialog"
      :post="editDialog_post"
    ></post-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions } from 'vuex'

import PSocialMediaIcon from '@/components/PSocialMediaIcon'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'

import PostViewDialog from './PostViewDialog'
import PostEditDialog from './PostEditDialog/index'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'

import eventBus from '@/eventBus'


export default {
  components: {
    PSocialMediaIcon,
    PSearchInput,
    PNoData,
    PBtnIcon,
    PostViewDialog,
    PostEditDialog
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      viewDialog: false,
      viewDialog_post: null,
      editDialog: false,
      editDialog_post: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 15
    }
  },


  computed: {
    ...mapGetters('socialMediaPost', ['socialMediaPostsByProject', 'socialMediaPostsLoadedByProject']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    postsComputed () {
      return this.socialMediaPostsByProject(this.currentProjectId).map(post => {
        return {
          ...post,
          changed: this.isItemChanged(post.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('socialMediaPost.table.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: this.$t('socialMediaPost.table.title'), value: 'title' },
        { text: this.$t('socialMediaPost.table.message'), value: 'message', width: 1 },
        { text: this.$t('socialMediaPost.table.channels'), value: 'channels', sortable: false, filterable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.socialMediaPostsByProject(this.currentProjectId).map(x => x.name)
      return items
    }
  },


  watch: {
    async currentProjectId () {
      this.startPageLoading()
      await Promise.all([
        this.loadSocialMediaPostsByProject(this.currentProjectId),
        this.loadEventsByProject(this.currentProjectId)
      ])
      this.stopPageLoading()
    }
  },


  methods: {
    ...mapActions('socialMediaPost', ['loadSocialMediaPostsByProject', 'loadSocialMediaPost', 'deleteSocialMediaPost']),
    ...mapActions('event', ['loadEventsByProject']),
    ...mapActions('eventCategory', ['loadEventCategories']),

    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchPosts(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        this.loadSocialMediaPostsByProject(this.currentProjectId)
      })
    },

    async searchPosts (val) {
      this.search = val
    },

    openViewDialog (post) {
      this.viewDialog_post = post
      this.viewDialog = true
    },

    async openEditDialog (post) {
      try {
        this.editDialog_post = post ? await this.loadSocialMediaPost(post) : null
        this.editDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async deletePost (post) {
      try {
        const confirm = await this.$confirm(this.$t('socialMediaPost.deletePostConfirm'), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteSocialMediaPost(post)
          this.showSnackbarSuccess(this.$t('socialMediaPost.postDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },

  async created () {
    try {
      this.initEventBus()
      this.startPageLoading()
      await Promise.all([
        this.loadSocialMediaPostsByProject(this.currentProjectId),
        this.loadEventsByProject(this.currentProjectId),
        this.loadEventCategories()
      ])
      if (this.$router.currentRoute.params?.goto) {
        await this.searchPosts(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
